import { css } from "@linaria/core"
import { type IObservableValue, observable, runInAction } from "mobx"
import codemark from "prosemirror-codemark"
import { baseKeymap, setBlockType, toggleMark, wrapIn } from "prosemirror-commands"
import { history, redo, undo } from "prosemirror-history"
import { InputRule, inputRules } from "prosemirror-inputrules"
import { keymap } from "prosemirror-keymap"
import { autoLinkingPlugin, linksKey, LinksMetaType } from "prosemirror-link-plugin"
import { liftListItem, sinkListItem, splitListItem, wrapInList } from "prosemirror-schema-list"
import { EditorState, Plugin, Selection } from "prosemirror-state"
import { Decoration, DecorationSet, EditorView } from "prosemirror-view"
import { type ChangeEvent } from "react"

import { parser, schema, serializer } from "@/components/PromptInput/ProseMirrorMarkdown.ts"
import { extractMentions, mentionDecoration, mentionPattern } from "@/components/PromptInput/ProseMirrorMentions.ts"
import { Model } from "@/lib/Model.ts"

import type { PromptInputModel } from "@/components/PromptInput/PromptInputModel.ts"
import type { TD_MemberProfile } from "@/lib/client/generated/TypeDefinitions.ts"
import type { MarkType } from "prosemirror-model"
import type { Command, Transaction } from "prosemirror-state"
import type { RefObject } from "react"

export class PromptTextAreaProseMirrorModel extends Model {
  readonly editorView: IObservableValue<EditorView | null> = observable.box(null)
  private readonly initialValue: string

  private readonly codemarkPluginEnabled: boolean
  private readonly autoLinkingPluginEnabled = false // Very buggy, enable when https://gitlab.com/emergence-engineering/prosemirror-link-plugin/-/issues/1 is fixed

  private createMarkdownInputRule(
    pattern: RegExp,
    markType: MarkType,
    markerPrefix: string
  ) {
    return new InputRule(pattern, (state, match, start, end) => {
      if (!markType) return null

      const hasPrefix = !match[0].startsWith(markerPrefix)
      const prefixLen = hasPrefix ? 1 : 0

      const openStart = start + prefixLen
      const openEnd = openStart + markerPrefix.length
      const textContent = match[2]

      const tr = state.tr
        .delete(openEnd + textContent.length, end) // Remove closing marker
        .delete(openStart, openEnd) // Remove opening marker
        .addMark(openStart, openStart + textContent.length, markType.create())

      // Insert space without the mark only if we're at the end of the document
      const isAtEnd = end >= state.doc.content.size - 1
      if (isAtEnd) {
        tr.removeStoredMark(markType)
        tr.insertText(" ", openStart + textContent.length)
      }

      return tr
    })
  }

  private createPlaceholderPlugin(placeholder: string) {
    return new Plugin({
      props: {
        decorations: (state) => {
          const doc = state.doc
          if (doc.childCount === 1 && doc.firstChild?.type.name === "paragraph" && doc.firstChild.childCount === 0) {
            const placeholderDecoration = Decoration.widget(1, () => {
              const span = document.createElement("span")
              span.className = ProseMirrorPlaceholder
              span.textContent = placeholder
              return span
            })
            return DecorationSet.create(doc, [placeholderDecoration])
          }
          return DecorationSet.empty
        }
      }
    })
  }

  private createMarkdownInputRules() {
    const rules: InputRule[] = []

    // Bold: **text** or __text__
    rules.push(this.createMarkdownInputRule(/(?:^|[^*])(\*\*)([^*]+)(\*\*)$/, schema.marks.strong, "**"))
    rules.push(this.createMarkdownInputRule(/(?:^|[^_])(__([^_]+)__)$/, schema.marks.strong, "__"))

    // Italic: *text* or _text_
    rules.push(this.createMarkdownInputRule(/(?:^|[^*])(\*)([^*]+)(\*)$/, schema.marks.em, "*"))
    rules.push(this.createMarkdownInputRule(/(?:^|[^_])(_([^_]+)_)$/, schema.marks.em, "_"))

    // Strikethrough: ~~text~~
    rules.push(this.createMarkdownInputRule(/(?:^|[^~])(~~([^~]+)~~)$/, schema.marks.strikethrough, "~~"))

    // Headers: # through ###### followed by space
    for (let level = 1; level <= 6; level++) {
      const hashes = "#".repeat(level)
      rules.push(new InputRule(new RegExp(`^${hashes}\\s$`), (state, _match, start, end) => {
        const $start = state.doc.resolve(start)

        if ($start.parent.type.name !== "paragraph") {
          return null
        }

        const tr = state.tr.delete(start, end)
        const pos = tr.mapping.map(start)

        return tr.setBlockType(pos, pos, schema.nodes.heading, { level })
      }))
    }

    // Ordered list: 1. followed by space
    rules.push(new InputRule(/^1\.\s$/, (state, _match, start, end) => {
      const $start = state.doc.resolve(start)

      if ($start.parent.type.name !== "paragraph") {
        return null
      }

      const tr = state.tr.delete(start, end)

      // Apply wrapInList to the state after deletion
      const wrappedState = state.apply(tr)

      if (wrapInList(schema.nodes.ordered_list)(wrappedState, (transaction) => {
        transaction.steps.forEach(step => tr.step(step))
      })) {
        return tr
      }

      return null
    }))

    // Bullet list: * followed by space
    rules.push(new InputRule(/^\*\s$/, (state, _match, start, end) => {
      const $start = state.doc.resolve(start)

      if ($start.parent.type.name !== "paragraph") {
        return null
      }

      const tr = state.tr.delete(start, end)

      // Apply wrapInList to the state after deletion
      const wrappedState = state.apply(tr)

      if (wrapInList(schema.nodes.bullet_list)(wrappedState, (transaction) => {
        transaction.steps.forEach(step => tr.step(step))
      })) {
        return tr
      }

      return null
    }))

    // Blockquote: > followed by space
    rules.push(new InputRule(/^>\s$/, (state, _match, start, end) => {
      const $start = state.doc.resolve(start)

      if ($start.parent.type.name !== "paragraph") {
        return null
      }

      const tr = state.tr.delete(start, end)

      const wrappedState = state.apply(tr)

      if (wrapIn(schema.nodes.blockquote)(wrappedState, (transaction) => {
        transaction.steps.forEach(step => tr.step(step))
      })) {
        return tr
      }

      return null
    }))

    // Code block: ``` (triple backticks)
    rules.push(new InputRule(/```$/, (state, _match, start, end) => {
      const $start = state.doc.resolve(start)
      const codeBlockType = schema.nodes.code_block

      if ($start.parent.type.name !== "paragraph") {
        return null
      }

      const textBeforeMatch = $start.parent.textContent.substring(0, $start.parentOffset - 3)
      const isAtLineStart = textBeforeMatch.length === 0

      let tr: Transaction

      if (isAtLineStart) {
        // Replace the paragraph containing ``` with a code block
        tr = state.tr.replaceRangeWith(
          $start.before(),
          $start.after(),
          codeBlockType.create()
        )
        // Position cursor inside the code block
        const resolvedPos = tr.doc.resolve($start.before() + 1)
        tr.setSelection(Selection.near(resolvedPos))
      } else {
        // Keep the text before ```, insert a newline, then a code block
        // Delete the ``` first
        tr = state.tr.delete(start, end)

        // Split the paragraph at the current position
        tr = tr.split(start)

        // After split, the new empty paragraph is at position start + 1
        // We need to convert this paragraph to a code block
        const newParagraphPos = start + 1

        tr = tr.setBlockType(newParagraphPos, newParagraphPos + 1, codeBlockType)

        // Position cursor inside the code block
        tr.setSelection(Selection.near(tr.doc.resolve(newParagraphPos)))
      }

      return tr
    }))

    return inputRules({ rules })
  }

  constructor(
    private readonly promptInputModel: PromptInputModel,
    private readonly value: IObservableValue<string>,
    private readonly onCancel: (() => void) | undefined,
    private readonly onSubmit: (() => void) | undefined,
    private readonly onChange: ((event: ChangeEvent<HTMLTextAreaElement>) => void) | undefined,
    private readonly profile: TD_MemberProfile | null,
    private readonly richText: boolean = true
  ) {
    super("PromptTextAreaProseMirrorModel")
    this.initialValue = value.get()
    this.codemarkPluginEnabled = richText
  }

  initializeEditor(
    container: HTMLElement,
    placeholder: string,
    autoFocus: boolean,
    readOnly: boolean,
    ref?: RefObject<HTMLTextAreaElement | null>
  ): EditorView {
    const initialMentions = extractMentions(this.value.get())

    const thirdPartyPlugins = []

    if (this.richText && this.codemarkPluginEnabled) {
      thirdPartyPlugins.push(...codemark({ markType: schema.marks.code }))
    }

    if (this.richText && this.autoLinkingPluginEnabled) {
      thirdPartyPlugins.push(autoLinkingPlugin(
        initialMentions,
        (start, end, alias, matchPos, pluginState) => {
          return mentionDecoration(start, end, alias, matchPos, pluginState, this.profile)
        },
        undefined,
        undefined,
        () => mentionPattern
      ))
    }

    const plugins = [
      history(),
      this.createPlaceholderPlugin(placeholder)
    ]

    if (this.richText) {
      plugins.push(this.createMarkdownInputRules())
    }

    const keymapBindings: Record<string, Command> = {
      "Mod-z": undo,
      "Mod-y": redo,
      "Mod-Shift-z": redo,
      "Enter": this.handleEnter,
      "Shift-Enter": (state, dispatch) => {
        const listItemType = schema.nodes.list_item
        if (listItemType && splitListItem(listItemType)(state, dispatch)) {
          return true
        }
        return baseKeymap["Enter"](state, dispatch)
      },
      "Escape": this.handleEscape,
      "ArrowDown": this.handleArrowDown,
      "ArrowUp": this.handleArrowUp,
      "Tab": (state, dispatch) => {
        const listItemType = schema.nodes.list_item
        if (listItemType && sinkListItem(listItemType)(state, dispatch)) {
          return true
        }

        if (this.promptInputModel.completionModel.isVisible.get()) {
          return this.handleEnter()
        }

        return false
      },
      "Shift-Tab": (state, dispatch) => {
        const listItemType = schema.nodes.list_item
        if (listItemType) {
          return liftListItem(listItemType)(state, dispatch)
        }
        return false
      }
    }

    if (this.richText) {
      keymapBindings["Mod-b"] = toggleMark(schema.marks.strong)
      keymapBindings["Mod-i"] = toggleMark(schema.marks.em)
      keymapBindings["Mod-`"] = toggleMark(schema.marks.code)
      keymapBindings["Mod-Shift-x"] = toggleMark(schema.marks.strikethrough)
      keymapBindings["Mod-Shift-c"] = setBlockType(schema.nodes.code_block)
      keymapBindings["Mod-Shift-b"] = wrapIn(schema.nodes.blockquote)
      keymapBindings["Mod-Shift-8"] = wrapInList(schema.nodes.bullet_list)
      keymapBindings["Mod-Shift-9"] = wrapInList(schema.nodes.ordered_list)
      keymapBindings["Ctrl-Shift-1"] = setBlockType(schema.nodes.heading, { level: 1 })
      keymapBindings["Ctrl-Shift-2"] = setBlockType(schema.nodes.heading, { level: 2 })
      keymapBindings["Ctrl-Shift-3"] = setBlockType(schema.nodes.heading, { level: 3 })
      keymapBindings["Ctrl-Shift-4"] = setBlockType(schema.nodes.heading, { level: 4 })
      keymapBindings["Ctrl-Shift-5"] = setBlockType(schema.nodes.heading, { level: 5 })
      keymapBindings["Ctrl-Shift-6"] = setBlockType(schema.nodes.heading, { level: 6 })
      keymapBindings["Ctrl-Shift-0"] = setBlockType(schema.nodes.paragraph)
    }

    plugins.push(keymap(keymapBindings))
    plugins.push(keymap(baseKeymap))
    plugins.push(...thirdPartyPlugins)

    const state = EditorState.create({
      doc: parser.parse(this.value.get()),
      plugins: plugins
    })

    const handlePaste = (view: EditorView, event: Event, clipboardData: DataTransfer | null) => {
      const text = clipboardData?.getData("text/plain")
      const isHtml = clipboardData?.types.includes("text/html")

      // Handle paste of plain text
      if (text && !isHtml && this.richText) {
        event.preventDefault()

        const { state } = view
        const { from, to } = state.selection

        // Parse the pasted text as Markdown
        const parsedDoc = parser.parse(text)

        // Wrap indented text in triple backticks, parse as Markdown, paste as rich text
        const hasIndentedLine = text.split(/\r?\n/).some(line => /^\s+/.test(line))
        if (parsedDoc.children.length === 1 && parsedDoc.children[0].type.name === "paragraph" && hasIndentedLine) {
          const doc = view.state.doc
          const currentText = doc.textContent
          const atLineStart = from === 0 || currentText[from - 1] === "\n"
          const trimmedText = text.replace(/^\n+|\n+$/g, "")
          const codeBlock = parser.parse(`${atLineStart ? "" : "\n"}\`\`\`\n${trimmedText}\n\`\`\``)

          const tr = state.tr.replaceWith(from, to, codeBlock.content)
          view.dispatch(tr)

          return true
        }

        // Paste as rich text
        // When the document is empty, replace the entire document to avoid adding an extra paragraph
        const isEmpty = state.doc.content.size === 0 || (state.doc.content.size === 2 && state.doc.textContent === "")
        const tr = isEmpty
          ? state.tr.replaceWith(0, state.doc.content.size, parsedDoc.content)
          : state.tr.replaceWith(from, to, parsedDoc.content)
        view.dispatch(tr)

        return true
      }

      if (text && isHtml && !this.richText) {
        event.preventDefault()

        const { state } = view
        const { from, to } = state.selection

        // Insert plain text only
        const tr = state.tr.insertText(text, from, to)
        view.dispatch(tr)

        return true
      }

      // Delegate to the default paste handler
      this.promptInputModel.handlePaste(event, clipboardData)
      return false
    }

    const view = new EditorView(container, {
      state,
      dispatchTransaction: this.dispatchTransaction,
      handleDOMEvents: {
        focus: () => {
          this.promptInputModel.onFocus()
          return false
        },
        blur: () => {
          this.promptInputModel.onBlur()
          return false
        },
        paste: (view, event) => {
          return handlePaste(view, event, event.clipboardData)
        }
      },
      editable: () => !readOnly
    })

    runInAction(() => {
      this.editorView.set(view)
    })

    if (ref) {
      ref.current = view.dom as HTMLTextAreaElement
    }

    if (autoFocus) {
      view.focus()
      view.dispatch(view.state.tr.setSelection(Selection.atEnd(view.state.doc)))
    }

    return view
  }

  private handleEnter = (): boolean => {
    if (this.promptInputModel.completionModel.isVisible.get()) {
      const items = this.promptInputModel.completionModel.items.get()
      const selectionIndex = this.promptInputModel.completionModel.selectionIndex.get()
      if (selectionIndex >= 0 && selectionIndex < items.length) {
        this.promptInputModel.completionModel.complete(items[selectionIndex])
      }
      return true
    }

    if (this.onSubmit && this.promptInputModel.canSubmit.get() && this.isModified()) {
      this.onSubmit()
      return true
    }

    if (this.promptInputModel.canSubmit.get() && this.promptInputModel.uploadComplete.get() && this.isModified()) {
      void this.promptInputModel.doSubmit()
      return true
    }

    // Always return true, Enter either submits or does nothing
    return true
  }

  private handleEscape = (): boolean => {
    if (this.promptInputModel.completionModel.isVisible.get()) {
      this.promptInputModel.completionModel.close()
      return true
    }
    if (this.onCancel && !this.isModified()) {
      this.onCancel()
      return true
    }
    return false
  }

  private isModified = (): boolean => {
    const view = this.editorView.get()
    if (view) {
      const currentValue = serializer.serialize(view.state.doc)
      if (currentValue !== this.initialValue) {
        return true
      }

      // It's OK to post an empty message (if other conditions are met)
      if (currentValue.trim().length === 0 && this.initialValue.trim().length === 0) {
        return true
      }
    }

    return false
  }

  private handleArrowDown = (state: EditorState, dispatch?: (tr: Transaction) => void): boolean => {
    if (this.promptInputModel.completionModel.isVisible.get()) {
      this.promptInputModel.completionModel.down()
      return true
    }

    // Check if the cursor is at the end of a code block, insert a new paragraph after the code block if that
    // code block is the last node in the document
    const { $from } = state.selection
    if ($from.parent.type === schema.nodes.code_block) {
      const parentPos = $from.before()
      const parentNode = state.doc.nodeAt(parentPos)
      if (parentNode && $from.parentOffset === parentNode.content.size) {
        const afterPos = $from.after()

        if (afterPos >= state.doc.content.size) {
          if (dispatch) {
            const tr = state.tr.insert(afterPos, schema.nodes.paragraph.create())
            tr.setSelection(Selection.near(tr.doc.resolve(afterPos + 1)))
            dispatch(tr)
          }
          return true
        }
      }
    }

    // Check if the cursor is at the end of a list item in a list, insert a new paragraph after the list if that
    // list is the last node in the document
    if ($from.depth >= 3 && $from.node($from.depth - 1).type === schema.nodes.list_item) {
      const paragraphNode = $from.parent
      const listItemDepth = $from.depth - 1
      const listDepth = $from.depth - 2

      const listItemNode = $from.node(listItemDepth)
      const listNode = $from.node(listDepth)
      const listPos = $from.before(listDepth)

      if ($from.parentOffset === paragraphNode.content.size) {
        const listItemPos = $from.before(listItemDepth)
        const isLastItem = listItemPos + listItemNode.nodeSize === listPos + listNode.nodeSize - 1

        if (isLastItem) {
          const afterListPos = listPos + listNode.nodeSize

          if (afterListPos >= state.doc.content.size) {
            if (dispatch) {
              const tr = state.tr.insert(afterListPos, schema.nodes.paragraph.create())
              tr.setSelection(Selection.near(tr.doc.resolve(afterListPos + 1)))
              dispatch(tr)
            }
            return true
          }
        }
      }
    }

    // Check if the cursor is at the end of a blockquote, insert a new paragraph after the blockquote if that
    // blockquote is the last node in the document
    if ($from.depth >= 2 && $from.node($from.depth - 1).type === schema.nodes.blockquote) {
      const paragraphNode = $from.parent
      const blockquoteDepth = $from.depth - 1
      const blockquoteNode = $from.node(blockquoteDepth)
      const blockquotePos = $from.before(blockquoteDepth)
      if ($from.parentOffset === paragraphNode.content.size) {
        const afterBlockquotePos = blockquotePos + blockquoteNode.nodeSize
        if (afterBlockquotePos >= state.doc.content.size) {
          if (dispatch) {
            const tr = state.tr.insert(afterBlockquotePos, schema.nodes.paragraph.create())
            tr.setSelection(Selection.near(tr.doc.resolve(afterBlockquotePos + 1)))
            dispatch(tr)
          }
          return true
        }
      }
    }


    return false
  }

  private handleArrowUp = (state: EditorState, dispatch?: (tr: Transaction) => void): boolean => {
    if (this.promptInputModel.completionModel.isVisible.get()) {
      this.promptInputModel.completionModel.up()
      return true
    }

    // Check if the cursor is at the beginning of a code block, insert a new paragraph before the code block if that
    // code block is the first node in the document
    const { $from } = state.selection
    if ($from.parent.type === schema.nodes.code_block) {
      const parentPos = $from.before()
      if ($from.parentOffset === 0 && parentPos === 0) {
        if (dispatch) {
          const tr = state.tr.insert(0, schema.nodes.paragraph.create())
          tr.setSelection(Selection.near(tr.doc.resolve(1)))
          dispatch(tr)
        }
        return true
      }
    }

    // Check if the cursor is at the beginning of a list item in a list, insert a new paragraph before the list if that
    // list is the first node in the document
    if ($from.depth >= 3 && $from.node($from.depth - 1).type === schema.nodes.list_item) {
      const listItemDepth = $from.depth - 1
      const listDepth = $from.depth - 2

      const listPos = $from.before(listDepth)
      const listItemPos = $from.before(listItemDepth)

      if ($from.parentOffset === 0 && listItemPos === listPos + 1 && listPos === 0) {
        if (dispatch) {
          const tr = state.tr.insert(0, schema.nodes.paragraph.create())
          tr.setSelection(Selection.near(tr.doc.resolve(1)))
          dispatch(tr)
        }
        return true
      }
    }

    // Check if the cursor is at the beginning of a blockquote, insert a new paragraph before the blockquote if that
    // blockquote is the first node in the document
    if ($from.depth >= 2 && $from.node($from.depth - 1).type === schema.nodes.blockquote) {
      const blockquoteDepth = $from.depth - 1
      const blockquotePos = $from.before(blockquoteDepth)
      if ($from.parentOffset === 0 && blockquotePos === 0) {
        if (dispatch) {
          const tr = state.tr.insert(0, schema.nodes.paragraph.create())
          tr.setSelection(Selection.near(tr.doc.resolve(1)))
          dispatch(tr)
        }
        return true
      }
    }

    return false
  }

  private dispatchTransaction = (transaction: Transaction) => {
    const view = this.editorView.get()
    if (!view) return

    let newState = view.state.apply(transaction)

    const markdown = serializer.serialize(newState.doc)
    const cursorPos = newState.selection.from

    if (this.autoLinkingPluginEnabled) {
      const mentions = extractMentions(markdown, cursorPos)

      const mentionTr = newState.tr.setMeta(linksKey, {
        type: LinksMetaType.linkUpdate,
        specs: mentions
      })

      newState = newState.apply(mentionTr)
    }

    view.updateState(newState)

    if (this.onChange) {
      this.onChange({
        target: { value: markdown, selectionStart: cursorPos },
        currentTarget: { value: markdown, selectionStart: cursorPos }
      } as ChangeEvent<HTMLTextAreaElement>)
    } else {
      runInAction(() => {
        this.promptInputModel.firstInputValue.set(markdown)
      })
    }

    if (this.promptInputModel.selectedInputMode.get().extensions.mentions) {
      // Calculate markdown cursor position by cutting the document at cursor and serializing
      const docBeforeCursor = newState.doc.cut(0, cursorPos)
      const markdownBeforeCursor = serializer.serialize(docBeforeCursor)
      const markdownCursorPos = markdownBeforeCursor.length

      this.promptInputModel.completionModel.onChange(markdown, markdownCursorPos)
    }
  }

  updateContent(newText: string) {
    const view = this.editorView.get()
    if (!view) return

    const currentMarkdown = serializer.serialize(view.state.doc)

    if (currentMarkdown !== newText) {
      const { state } = view
      const newContent = parser.parse(newText).content

      // Force-insert a space after a complete mention. A partial mention will not have a trailing space.
      // A full mention does, but it's eaten by the Markdown parser.
      const shouldAddSpace = /@\S+\s$/.test(newText)

      let tr = state.tr.replaceWith(0, state.doc.content.size, newContent)

      if (shouldAddSpace) {
        const lastTextPos = tr.doc.content.size - 1
        tr = tr.insertText(" ", lastTextPos)
      }

      view.dispatch(tr)

      if (this.autoLinkingPluginEnabled) {
        const mentions = extractMentions(newText)
        view.dispatch(view.state.tr.setMeta(linksKey, {
          type: LinksMetaType.linkUpdate,
          specs: mentions
        }))
      }
    }
  }

  terminate() {
    const view = this.editorView.get()
    if (view) {
      view.destroy()
      runInAction(() => {
        this.editorView.set(null)
      })
    }
  }
}

const ProseMirrorPlaceholder = css`
  position: absolute;
  pointer-events: none;
  user-select: none;

  color: var(--text-color-40);
`